import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "./Button";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

export function Modal({
  title,
  eyebrow,
  closeLabel,
  onClose,
  children,
  className,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusInitial = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const initial =
        dialog?.querySelector<HTMLElement>('[data-autofocus="true"]') ??
        dialog?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])');
      initial?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusScope =
        dialog.querySelector<HTMLElement>('[role="alertdialog"]') ?? dialog;
      const focusable = Array.from(focusScope.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => !element.hasAttribute("hidden") && element.getClientRects().length > 0,
      );
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusInitial);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/35 p-3 backdrop-blur-[2px] sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-line bg-paper shadow-[var(--shadow-pop)] sm:max-h-[calc(100dvh-3rem)]",
          className,
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-5 py-4">
          <div className="min-w-0">
            {eyebrow ? <p className="eyebrow mb-1 text-brand">{eyebrow}</p> : null}
            <h2 id={titleId} className="truncate text-xl font-semibold text-ink">
              {title}
            </h2>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onCloseRef.current()}
            aria-label={closeLabel}
            className="shrink-0"
          >
            <X className="size-[18px]" />
          </Button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
